import React, { useCallback, useMemo } from 'react'

import { SessionProvider, signIn, signOut, useSession } from 'next-auth/react'

import {
  Auth0Context,
  Auth0ContextInterface,
  AuthState,
  GetTokenSilentlyOptions,
  RedirectLoginOptions,
  User,
} from './use-auth0'

export type AppState = {
  returnTo?: string
}

export interface Auth0ProviderOptions {
  children?: React.ReactNode
  context?: React.Context<Auth0ContextInterface>

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  audience: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clientId: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  domain: any
  onRedirectCallback?: (appState?: AppState, user?: User) => void

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  redirectUri: any
}

/**
 * ```jsx
 * <Auth0Provider
 *   audience={process.env.NEXT_PUBLIC_AUTH_AUDIENCE || ''}
 *   clientId={process.env.NEXT_PUBLIC_AUTH_CLIENT_ID || ''}
 *   domain={process.env.NEXT_PUBLIC_AUTH_DOMAIN || ''}
 *   onRedirectCallback={(appState) => {
 *     push(appState?.returnTo || window.location.origin)
 *   }}
 *   redirectUri={typeof window !== 'undefined' ? window.location.origin : ''}
 *   <MyApp />
 * </Auth0Provider>
 * ```
 *
 * Provides the Auth0Context to its child components.
 */
const Auth0ToNextAuthAdapterProvider = (opts: Auth0ProviderOptions): JSX.Element => {
  const {
    children,
    // skipRedirectCallback,
    // onRedirectCallback = defaultOnRedirectCallback,
    context = Auth0Context,
    // ...clientOpts
  } = opts

  const loginWithRedirect = useCallback((opts?: RedirectLoginOptions): Promise<void> => {
    const callbackUrl = (opts?.appState?.returnTo ?? '') + (opts?.appState?.target ?? '')
    const signInOptions = { callbackUrl: callbackUrl == '' ? undefined : callbackUrl }
    return signIn(undefined, signInOptions)
  }, [])

  const { status, data } = useSession()

  const getAccessTokenSilently = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (options?: GetTokenSilentlyOptions | undefined): Promise<string> =>
      // Provide 'authorization: Bearer ' token
      // next-auth.session-token (JWT) is HttpOnly and not available "on" client
      Promise.resolve(status === 'authenticated' ? data.accessToken : `Next-Auth-${status}`),
    [status, data]
  )

  const logout = useCallback(() => signOut(), [])

  const contextValue = useMemo(() => {
    const state: AuthState<User> = {
      isAuthenticated: status === 'authenticated',
      isLoading: status === 'loading',
    }
    if (status === 'authenticated') {
      if (data.user) {
        state.user = {
          // # email #
          // Used in src/modules/projects/hooks/useApplyYaml.ts
          email: data.user.email ?? undefined,

          // Used in src/pages/_app.tsx
          // Auth Error for >1st login without email verified
          //  pageComponent = <AuthErrorOverlay messageTitle={CONSOLE_AUTH0_AUTHENTICATION_ERROR_MESSAGE} />
          // '"We had an issue authenticating your account'

          email_verified: data.email_verified ?? false,

          // Dummy value - to be implemented
          logins_count: data.logins_count ?? 0,

          // # sub - "subject" or id #
          // Used in src/modules/user/VerificationErrorOverlay.tsx - resend verification email
          sub: 'string',
        }
      }
    }

    return {
      ...state,
      loginWithRedirect,
      getAccessTokenSilently,
      logout,
    }
  }, [loginWithRedirect, status, getAccessTokenSilently, logout, data])

  return <context.Provider value={contextValue}>{children}</context.Provider>
}

// Wrap the inner provider with next-auth SessionProvider
const Auth0Provider = (opts: Auth0ProviderOptions): JSX.Element => {
  return (
    <SessionProvider basePath="/console-api/auth/" refetchOnWindowFocus={false}>
      <Auth0ToNextAuthAdapterProvider
        audience={opts.audience}
        clientId={opts.clientId}
        domain={opts.domain}
        onRedirectCallback={opts.onRedirectCallback}
        redirectUri={opts.redirectUri}
      >
        {opts.children}
      </Auth0ToNextAuthAdapterProvider>
    </SessionProvider>
  )
}

export { Auth0Provider }
